import { ModelType, useModel } from '@/shared'
import { Ref, computed, reactive, ref, watch } from 'vue'
import { useGlobalToast as useToast } from '@/shared/lib/toast'
import { userIsAdmin } from '@/shared'
import { useSalespointStore, useWebsocketStore } from '@/store'
import { MortgageModel, createMortgageModel } from './mortgage-model'
import { mortgageApi } from '../api'
import { storeToRefs } from 'pinia'
import { useAuthModel } from '@/features/auth'

const LC_MORTGAGE_LIST_PAGE_SIZE = 'mortgage_list_page_size'
const LC_MORTGAGE_FILTER = 'mortgage_table_filters'

function getMortgageListPageSizeFromLS() {
  const baseValue = 5
  return Number(localStorage.getItem(LC_MORTGAGE_LIST_PAGE_SIZE)) || baseValue
}

function setMortgageListPageSizeToLS(pageSize: number) {
  localStorage.setItem(LC_MORTGAGE_LIST_PAGE_SIZE, pageSize.toString())
}

export type MortgageTableFilter = {
  id: number | null
  first_name: string | null
  middle_name: string | null
  last_name: string | null
  status: string[] | null
  salespoint_id: number | null
  date1: string | null
  date2: string | null
  credit_amount: string | null
  bankName: string | null
}

export type MortgageTableModel = {
  mortgageModels: Ref<MortgageModel[] | null>
  startIndex: Ref<number>
  pageNumber: Ref<number>
  pageSize: Ref<number>
  totalSize: Ref<number>
  getMortgages(): void
  getMortgagesPending: Ref<boolean>
  pending: Ref<boolean>
  listPageActive: Ref<boolean>
  setListPageActive(active: boolean): void
  filter: {
    id: number | null
    last_name: string | null
    status: string[] | null
    salespoint_id: number | null
    date1: string | null
    date2: string | null
    credit_amount: string | null
  }
  drop(): void
}

const createMortgageTableModel = (): MortgageTableModel => {
  // Уведомления
  const { add: addToast } = useToast()
  // Store авторизации. Тут нужен для получения текущего юзера
  const authModel = useAuthModel()
  // Store с выбранной тт
  const { salespoint } = storeToRefs(useSalespointStore())
  // Сообщения из сокета
  const { message } = storeToRefs(useWebsocketStore())
  // Список моделей. Список заявок обернутых в модели
  const mortgageModels = ref<MortgageModel[] | null>(null) as Ref<MortgageModel[] | null>
  // Размер страницы
  const pageSize = ref<number>(getMortgageListPageSizeFromLS())
  // Номер страницы
  const pageNumber = ref<number>(0)
  // Общие кол-во найденных заявок.
  const totalSize = ref<number>(0)
  // Начальный индекс. Начиная с какого индекса нам нужны заявки
  const startIndex = computed(() => pageNumber.value * pageSize.value)
  // Индикатор загрузки
  const pending = ref<boolean>(false)
  /**
   * Список заявок должен грузиться только на странице списка.
   * Store создаётся глобально в App.vue, поэтому без флага getMortgages
   * срабатывает и на /mortgage/app/:id (лишние program/user/salespoint/bankapp).
   */
  const listPageActive = ref(false)

  // Загружаем фильтр при инициализации
  const savedFilter = (() => {
    const raw = localStorage.getItem(LC_MORTGAGE_FILTER)
    if (!raw) return null
    try {
      return JSON.parse(raw)
    } catch {
      return null
    }
  })()

  // Фильтр поиска
  const filter = reactive<MortgageTableFilter>({
    id: null,
    first_name: null,
    middle_name: null,
    last_name: null,
    status: null,
    salespoint_id: null,
    date1: null,
    date2: null,
    credit_amount: null,
    bankName: null,
    ...(savedFilter || {})
  })
  // Api для получения списка заявок
  const {
    post: getListPost,
    pending: getMortgagesPending,
    abort: getListAbort,
    error: getListError
  } = mortgageApi.getList()

  // Сохраняет размер таблицы в локальном хранилище
  watch(pageSize, pageSize => setMortgageListPageSizeToLS(pageSize))

  // Обновляет список при изменении страницы или размера таблицы или выбранной ТТ
  watch([pageSize, pageNumber, salespoint], () => {
    if (!listPageActive.value) {
      // Пока список не открыт — сбрасываем кэш, чтобы при возврате подтянуть актуальные данные
      mortgageModels.value = null
      return
    }

    getMortgages()
  })

  // Сохраняем фильтры при изменении
  watch(
    filter,
    newVal => {
      localStorage.setItem(LC_MORTGAGE_FILTER, JSON.stringify(newVal))
    },
    { deep: true }
  )

  // Следим за сокетом только на странице списка
  watch(message, message => {
    if (!message || !listPageActive.value) {
      return
    }

    const { _type, _data } = message
    if (
      authModel.user &&
      salespoint.value &&
      // Если статус сообщения === "создание заявки"
      _type === 'mortgage_create' &&
      // Если юзер админ или заявка привязана к ТТ, которая привязана к юзеру
      (userIsAdmin(authModel.user) || authModel.user.salespoint_staff.includes(_data.app.salespoint_id)) &&
      // Если заявки еще нет в списке. Есть возможность, что добавят руками заявку, например, при копировании передадут в массив сразу и
      // поэтому проверяем, что заявка еще не в списке
      !mortgageModels.value?.find(model => model.mortgage?.id === _data.app.id)
    ) {
      const app = _data.app
      const model = createMortgageModel(app)
      const stopWatch = watch(
        model,
        model => {
          if (model.salespoint && model.user) {
            mortgageModels.value?.unshift(model)
            stopWatch()
          }
        },
        { deep: true }
      )
    }
  })

  function setListPageActive(active: boolean) {
    const wasActive = listPageActive.value
    listPageActive.value = active

    if (!active) {
      getListAbort()
      pending.value = false
      // Сбрасываем модели списка: иначе computedAsync/websocket продолжают
      // дергать program/user/salespoint/bankapp, пока открыта страница заявки
      mortgageModels.value = null
      return
    }

    if (!wasActive && !mortgageModels.value) {
      getMortgages()
    }
  }

  function drop() {
    mortgageModels.value = null
    pageNumber.value = 0
    totalSize.value = 0
    pending.value = false
    filter.id = null
    filter.first_name = null
    filter.middle_name = null
    filter.last_name = null
    filter.status = null
    filter.salespoint_id = null
    filter.date1 = null
    filter.date2 = null
    filter.credit_amount = null
  }

  const BANK_STATUS_MAP: Record<string, string> = {
    app_create: 'app_create',
    app_wait: 'wait',
    app_wait_accept: 'wait_accept',
    app_bank_note: 'bank_note',
    app_complete_accept: 'accept',
    app_complete_decline: 'decline',
    app_ending: 'ending',
    app_issued: 'issued',
    app_cancel: 'cancel'
  }

  // Функция, которая создает данные фильтрации подходящие под бекенд
  // В зависимости от прав юзера и значений фильтра
  function getFilterForGetMortgages() {
    // Если пользователь не авторизован, то ничего не возвращаем
    if (!authModel.user) return

    // Торговые точки по которым идет фильтрация. Если значение undefined, то в json он не пропишется и
    // значение для данной переменной не пойдет в фильтрацию - подтянет заявки всех ТТ
    let salespoint_id: number[] | undefined = undefined
    // Если в фильтре выбрана ТТ
    if (filter.salespoint_id) {
      // Записываем выбранную в фильтре тт. Подтянет заявки только выбранной ТТ
      salespoint_id = [filter.salespoint_id]
    }
    // Если пользователь не админ и не выбрал в фильтре ТТ
    else if (!userIsAdmin(authModel.user)) {
      // Получает только привязанные к пользователю ТТ
      salespoint_id = authModel.user.salespoint_staff
    }

    let firstName: string | undefined
    let lastName: string | undefined
    let middleName: string | undefined

    const fioRaw = (filter.last_name ?? '').trim()
    if (fioRaw) {
      const parts = fioRaw.split(/\s+/).filter(Boolean)
      firstName = parts[1]
      lastName = parts[0]
      middleName = parts[2]
    }

    const mappedCreditAmount = Array.isArray(filter.status)
      ? filter.status.map(s => BANK_STATUS_MAP[s]).filter(Boolean)
      : BANK_STATUS_MAP[filter.status ?? ''] ?? undefined

    return {
      salespoint_id,
      id__icontains: filter.id || undefined,
      main_customer__first_name__icontains: firstName || undefined,
      main_customer__last_name__icontains: lastName || undefined,
      main_customer__middle_name__icontains: middleName || undefined,
      created_at__date__gte: filter.date1 || undefined,
      created_at__date__lte: filter.date2 || undefined,
      credit_amount: filter.credit_amount || undefined,
      bank_app_status__status:
        mappedCreditAmount && Array.isArray(mappedCreditAmount) && mappedCreditAmount.length >= 1
          ? mappedCreditAmount
          : undefined,
      bank_app_status__bank:
        Array.isArray(filter.bankName) && filter.bankName.length >= 1 ? filter.bankName : undefined
    }
  }

  // Получение списка заявок
  function getMortgages() {
    // Не грузим список вне страницы «Мои заявки»
    if (!listPageActive.value) return

    // Если юзер не авторизован или не выбрана ТТ, то нельзя запрашивать заявки
    if (!authModel.user || !salespoint.value) return

    // Если уже идет запрос
    if (pending.value) {
      // То нужно обрубить текущий
      getListAbort()
    }

    // Ставим индикатор загрузки активным
    pending.value = true

    // Запрос на получение списка заявок
    getListPost({
      // Фильтрация
      _filter: getFilterForGetMortgages(),
      // Сортирует список в обратном порядке
      _sort: '-created_at',
      // Размер страницы
      _page_size: pageSize.value,
      // Начальный индекс
      _start_index: startIndex.value
    }).then(res => {
      if (res?._code === 0 && res._data) {
        const modelList = res._data.map(mortgage => createMortgageModel(mortgage))
        if (modelList.length) {
          const stopWatch = watch(
            () => modelList,
            models => {
              const modelNoData = models?.find(model => !model.salespoint || !model.user)
              if (modelNoData) return
              stopWatch()
              pending.value = false
              mortgageModels.value = modelList
              if (res._data_total_size !== undefined) {
                totalSize.value = res._data_total_size
              }
            },
            { deep: true }
          )
        } else {
          pending.value = false
          mortgageModels.value = modelList
          if (res._data_total_size !== undefined) {
            totalSize.value = res._data_total_size
          }
        }
      } else if (getListError.value) {
        if (!((getListError.value as any) instanceof DOMException)) {
          addToast({ severity: 'error', summary: 'Получение списка заявок', detail: getListError })
        }
      }
    })
  }

  return {
    startIndex,
    mortgageModels,
    pageNumber,
    pageSize,
    totalSize,
    getMortgages,
    getMortgagesPending,
    pending,
    listPageActive,
    setListPageActive,
    filter,
    drop
  }
}

export const useMortgageTableModel = (type: ModelType, model?: MortgageTableModel) =>
  useModel('mortgage-send', createMortgageTableModel, type, model)
